import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation, Link, Outlet } from 'react-router-dom';
import { LayoutDashboard, Users, CreditCard, Tag, UserPlus, MessageSquare, Database, Wrench, ArrowLeft, Menu, X, Shield, LifeBuoy } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const NAV = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/accounts', icon: CreditCard, label: 'Accounts' },
  { path: '/admin/promo-codes', icon: Tag, label: 'Promo Codes' },
  { path: '/admin/affiliates', icon: UserPlus, label: 'Affiliates' },
  { path: '/admin/communication', icon: MessageSquare, label: 'Communication' },
  { path: '/admin/support', icon: LifeBuoy, label: 'Support' },
  { path: '/admin/data', icon: Database, label: 'Data Management' },
  { path: '/admin/maintenance', icon: Wrench, label: 'Maintenance' },
];

const AdminLayout = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSupportCount, setOpenSupportCount] = useState(0);

  useEffect(() => {
    if (!user?.is_admin || !token) return;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/support/admin/open-count`, { headers: { Authorization: `Bearer ${token}` } });
        setOpenSupportCount(res.data.open || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [user, token, location.pathname]);

  useEffect(() => {
    if (!user?.is_admin) navigate('/dashboard');
  }, [user, navigate]);

  if (!user?.is_admin) return null;

  const isActive = (nav) => nav.exact ? location.pathname === nav.path : location.pathname.startsWith(nav.path);

  return (
    <div className="min-h-screen bg-[#0f1a14] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#132218] border-r border-emerald-900/30 transform transition-transform lg:translate-x-0 lg:static lg:flex-shrink-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="admin-sidebar">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center gap-3 px-5 border-b border-emerald-900/30">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-green-500 flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="text-white font-semibold text-sm">Miadesk</span>
              <span className="text-emerald-400 text-xs block -mt-0.5">Admin Panel</span>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
            {NAV.map((nav) => {
              const active = isActive(nav);
              return (
                <Link
                  key={nav.path}
                  to={nav.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${active ? 'bg-emerald-600/20 text-emerald-300' : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'}`}
                  data-testid={`admin-nav-${nav.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <nav.icon className={`w-4.5 h-4.5 ${active ? 'text-emerald-400' : ''}`} />
                  <span className="flex-1">{nav.label}</span>
                  {nav.path === '/admin/support' && openSupportCount > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="support-open-badge">
                      {openSupportCount > 99 ? '99+' : openSupportCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Back to app */}
          <div className="p-3 border-t border-emerald-900/30">
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors" data-testid="admin-back-to-app">
              <ArrowLeft className="w-4.5 h-4.5" /> Back to App
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 flex items-center justify-between px-6 border-b border-emerald-900/30 bg-[#0f1a14]/80 backdrop-blur-sm relative">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-emerald-600 via-green-500 to-teal-500" />
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-gray-400 hover:text-white" data-testid="admin-menu-toggle">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-sm text-gray-500">Logged in as</span>
            <span className="text-sm text-emerald-400 font-medium">{user?.email}</span>
          </div>
        </header>
        <main className="flex-1 p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
